import {
  EyeIcon,
  PlusIcon,
  PlayIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import {
  v2WatchlistsList,
  v2WatchlistsCreate,
  v2WatchlistsDelete,
  v2WatchlistsAddItem,
  v2WatchlistsRemoveItem,
  v2WatchlistsRun,
} from "../services/tradingAgentsV2";

export default function Watchlists() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [tickers, setTickers] = useState("");
  const [schedule, setSchedule] = useState("manual");
  const [addTicker, setAddTicker] = useState<Record<number, string>>({});

  const query = useQuery({
    queryKey: ["watchlists"],
    queryFn: v2WatchlistsList,
    refetchInterval: 15000,
  });

  const createMutation = useMutation({
    mutationFn: v2WatchlistsCreate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      setName("");
      setTickers("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: v2WatchlistsDelete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["watchlists"] }),
  });

  const addItemMutation = useMutation({
    mutationFn: ({ wlId, ticker }: { wlId: number; ticker: string }) =>
      v2WatchlistsAddItem(wlId, ticker),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["watchlists"] }),
  });

  const removeItemMutation = useMutation({
    mutationFn: ({ wlId, itemId }: { wlId: number; itemId: number }) =>
      v2WatchlistsRemoveItem(wlId, itemId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["watchlists"] }),
  });

  const runMutation = useMutation({
    mutationFn: v2WatchlistsRun,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["watchlists"] }),
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const tickerList = tickers
      .split(",")
      .map((t) => t.trim().toUpperCase())
      .filter(Boolean);
    createMutation.mutate({ name: name.trim(), schedule, tickers: tickerList });
  };

  const watchlists = query.data?.watchlists ?? [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card lg:col-span-1">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Create Watchlist
          </h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Tech Stocks"
              required
            />
            <Input
              label="Tickers (comma-separated)"
              value={tickers}
              onChange={(e) => setTickers(e.target.value)}
              placeholder="AAPL, MSFT, GOOGL"
            />
            <div>
              <label className="label">Schedule</label>
              <select
                className="select w-full"
                value={schedule}
                onChange={(e) => setSchedule(e.target.value)}
              >
                <option value="manual">Manual</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            {createMutation.isError && (
              <p className="text-sm text-red-600">
                {(createMutation.error as Error).message}
              </p>
            )}
            <Button
              type="submit"
              className="w-full"
              isLoading={createMutation.isPending}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Create
            </Button>
          </form>
        </div>

        <div className="space-y-4 lg:col-span-2">
          {watchlists.length === 0 ? (
            <div className="card">
              <p className="text-sm text-gray-500">
                No watchlists yet. Create one to start monitoring tickers.
              </p>
            </div>
          ) : (
            watchlists.map((wl) => (
              <div
                key={wl.id}
                className="card border border-gray-200"
              >
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <EyeIcon className="h-5 w-5 text-primary-500" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {wl.name}
                    </h3>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {wl.schedule || "manual"}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => runMutation.mutate(wl.id)}
                      isLoading={runMutation.isPending}
                      leftIcon={<PlayIcon className="h-4 w-4" />}
                    >
                      Run All
                    </Button>
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(wl.id)}
                      className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {wl.last_run_at && (
                  <p className="mb-2 text-xs text-gray-500">
                    Last run: {new Date(wl.last_run_at).toLocaleString()}
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  {wl.items.map((item) => (
                    <div
                      key={item.id}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm"
                    >
                      <span className="font-medium text-gray-900">
                        {item.ticker}
                      </span>
                      {item.last_action && (
                        <span
                          className={`rounded px-1 text-xs font-medium ${
                            item.last_action.toUpperCase() === "BUY"
                              ? "bg-green-100 text-green-700"
                              : item.last_action.toUpperCase() === "SELL"
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {item.last_action}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          removeItemMutation.mutate({
                            wlId: wl.id,
                            itemId: item.id,
                          })
                        }
                        className="ml-0.5 text-gray-400 hover:text-red-500"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <form
                    className="inline-flex items-center gap-1"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const t = (addTicker[wl.id] || "").trim().toUpperCase();
                      if (t) {
                        addItemMutation.mutate({ wlId: wl.id, ticker: t });
                        setAddTicker((prev) => ({ ...prev, [wl.id]: "" }));
                      }
                    }}
                  >
                    <input
                      className="w-20 rounded-full border border-dashed border-gray-300 px-2 py-1 text-xs focus:border-primary-400 focus:outline-none"
                      placeholder="+ Add"
                      value={addTicker[wl.id] || ""}
                      onChange={(e) =>
                        setAddTicker((prev) => ({
                          ...prev,
                          [wl.id]: e.target.value,
                        }))
                      }
                    />
                  </form>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
